import {createApp, ref} from 'vue';
import {connectVue} from '../modules/configureBugsnag';
import configureGlobalComponents from '../modules/configureGlobalComponents';
import axiosClient from '../modules/axiosClient';
import VueAddress from '../components/global/address.vue';
import Slider from '../components/global/slider.vue';
import {AxiosResponse} from 'axios';
import useVuelidate from '@vuelidate/core';
import {email, helpers, maxLength, required} from '@vuelidate/validators';
import {phone} from '../modules/regitValidators';

type FormFieldDropdownOptions = {
    [key: string]: string
}

type DropdownOption = {
    value: string;
    label?: string;
}

type FormFieldDropdownOptionResolver = {
    url: string,
    parameters: {
        [key: string]: string,
    }
}

type FieldNameOverrides = {
    town: string,
    county: string,
    houseno: string,
    address1: string,
    address2: string,
    address3: string,
    postcode: string
}

type FieldOptions = {
    fieldNameOverrides?: FieldNameOverrides
    'message'?: string,
    'no-value'?: string,
    'yes-value'?: string,
    dropdownOptions?: FormFieldDropdownOptions,
    dropdownOptionResolver?: FormFieldDropdownOptionResolver,
}

type FormFieldConfiguration = {
    label: string | null,
    name: string,
    type: string,
    placeholder: string | null,
    value: any,
    options: FieldOptions,
    required: number
}

const app = createApp({
    props: ['formConfiguration'],
    data() {
        const dataObj = {
            dynamicOptions: {},
            disabled: false,
        } as any;

        // Initialize field data here to make it accessible via $data
        this.formConfiguration.fields.forEach((item: FormFieldConfiguration) => {
            if (item.type === 'address') {
                const defaultValue = item.value || {};
                dataObj[item.name] = {
                    postcode: defaultValue.postcode || '',
                    address1: defaultValue.address1 || '',
                    address2: defaultValue.address2 || '',
                    address3: defaultValue.address3 || '',
                    town: defaultValue.town || '',
                    county: defaultValue.county || '',
                };
            } else if (item.type === 'dropdown') {
                dataObj[item.name] = item.value || '';
            } else if (item.type === 'bool') {
                dataObj[item.name] = item.value === item.options['yes-value'];
            } else {
                dataObj[item.name] = item.value || '';
            }
        });

        return dataObj;
    },
    components: {
        'vue-address': VueAddress,
        'slider': Slider,
    },
    validations() {
        const rules = {} as any;

        this.formConfiguration.fields.forEach((item: FormFieldConfiguration) => {
            // rules
            rules[item.name] = {};
            if (item.type === 'address') {
                rules[item.name] = {
                    postcode: {
                        required: helpers.withMessage('Please enter your postcode', required),
                        maxLengthValue: maxLength(10),
                    },
                    address1: {
                        required,
                        maxLengthValue: maxLength(255),
                    },
                    address2: {
                        maxLengthValue: maxLength(255),
                    },
                    address3: {
                        maxLengthValue: maxLength(255),
                    },
                    town: {
                        required: helpers.withMessage('Please enter your town', required),
                        maxLengthValue: maxLength(255),
                    },
                    county: {
                        maxLengthValue: maxLength(255),
                    },
                };
            } else if (item.type === 'email') {
                rules[item.name] = {
                    email,
                };
            } else if (item.type === 'phone') {
                rules[item.name] = {
                    phone: helpers.withMessage('Please enter a valid telephone', phone),
                };
            }

            if (item.required) {
                if (item.type === 'dropdown') {
                    rules[item.name].required = helpers.withMessage('Please choose a value', required);
                } else {
                    rules[item.name].required = helpers.withMessage('Please enter a value', required);
                }
            }
        });

        return rules;
    },
    setup() {
        const v$ = useVuelidate();

        return {
            'v$': v$,
        };
    },
    methods: {
        async submitEnquiry(e: Event) {
            this.disabled = true;
            e.preventDefault();
            e.stopPropagation();

            const validationResult = await this.v$.$validate();
            if (validationResult) {
                window.sendAnalyticsEvent('Conversion Tracking', 'Lead Generation', 'Long form fill');
                console.log('submit');
                (this.$refs.enquiryForm as HTMLFormElement).submit();
            }

            setTimeout(() => {
                this.disabled = false;
            }, 5000);
        },
        fetchDropdownOptions(field: FormFieldConfiguration) {
            if (this.timeout) {
                clearTimeout(this.timeout);
            }

            this.timeout = setTimeout(() => {
                const resolver = field.options.dropdownOptionResolver;
                const postObject = {} as any;

                if (!resolver) {
                    return;
                }

                for (const parameter in resolver.parameters) {
                    if (resolver.parameters.hasOwnProperty(parameter)) {
                        const parameterProperty = resolver.parameters[parameter];
                        const currentValue = parameterProperty.split('.').reduce((o: any, i: any) => o[i], this.$data);
                        if (!currentValue) {
                            this.dynamicOptions[field.name] = undefined;
                            this.$data[field.name] = '';
                            return;
                        }
                        postObject[parameter] = currentValue;
                    }
                }

                axiosClient.post(resolver.url, postObject).then((response: AxiosResponse) => {
                    this.dynamicOptions[field.name] = response.data;
                    this.$data[field.name] = '';
                    if (this.dynamicOptions[field.name].length > 0) {
                        const firstItem = this.dynamicOptions[field.name][0] as DropdownOption;
                        this.$data[field.name] = firstItem.value;
                    }
                });
            }, 500);
        },
        registerDropdownWatcher(field: FormFieldConfiguration) {
            const resolver = field.options.dropdownOptionResolver;

            if (!resolver) {
                return;
            }

            for (const parameter in resolver.parameters) {
                if (resolver.parameters.hasOwnProperty(parameter)) {
                    const parameterProperty = resolver.parameters[parameter];
                    const instance = this;
                    this.$watch(parameterProperty, function() {
                        instance.fetchDropdownOptions(field);
                    });
                }
            }
        },
    },
    mounted() {
        const fields = this.formConfiguration.fields || [];
        fields.forEach((field: FormFieldConfiguration) => {
            if (field.type === 'dropdown' && field.options.dropdownOptionResolver !== undefined) {
                this.fetchDropdownOptions(field);
                this.registerDropdownWatcher(field);
            }
        });
        this.disabled = false;
    },
}, window.propsData);

connectVue(app);
configureGlobalComponents(app);
app.mount('#enquire-full');
